<!-- eslint-disable vue/no-deprecated-dollar-listeners-api -->
<template>
  <SListItem
    :class="$attrs.class"
    :style="$attrs.style"
    v-bind="!hasClickEvent ? filteredAttrs : {}"
    :icon-color="userOptions.color"
    :badge-title="badgeInTitle && isAdmin ? adminBadgeText[0].text : ''"
    :badges="!badgeInTitle ? adminBadgeText : []"
    :title="hasClickEvent ? '' : user.meta.displayname"
    :data-title="user.meta.displayname"
    :icon="userOptions.icon"
    :description="userDescription"
    :hoverable="hoverable"
    :right-action-icon="$hasSlotContent($slots['action']) ? undefined : 'icon-chevron-right'"
    class="sdui-user-item"
  >
    <template #item-title="{ ariaDescribedby }" v-if="hasClickEvent">
      <!-- eslint-disable-next-line sdui/require-focus-restore-on-disabled-button -->
      <b-button
        v-bind="filteredAttrs"
        variant="link"
        class="sdui-list-item__click-area text-left p-0 sdui-user-item-button d-flex align-items-center"
        :disabled="$attrs.disabled"
        :pressed="ariaPressed"
        :aria-describedby="ariaDescribedby"
        @click.prevent="$emit('click')"
      >
        <span class="sdui-list-item__title">{{ user.meta.displayname }}</span>
        <b-badge v-if="badgeInTitle && isAdmin"
                 variant="secondary"
                 class="small ml-1"
        >
          {{ adminBadgeText[0].text }}
        </b-badge>
      </b-button>
    </template>

    <template v-if="$hasSlotContent($slots['action'])" #right-action>
      <slot name="action" />
    </template>
  </SListItem>
</template>

<script>
import EntityMixin from 'src/mixins/miscellaneous/entityMixin.js'
import { User } from 'src/store/models/index.js'

export default {
  inheritAttrs: false,
  name: 'SduiUserListItem',
  mixins: [EntityMixin],
  props: {
    user: {
      type: [User, Object],
      default: () => new User(),
    },

    channelOptions: {
      type: Boolean,
      default: false,
    },

    channelUsers: {
      type: Boolean,
      default: false,
    },

    courseEnrollment: {
      type: Object,
      default: () => ({}),
    },

    badgeInTitle: {
      type: Boolean,
      default: false,
    },

    hoverable: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({}),
  computed: {
    filteredAttrs() {
      const { class: _cls, style: _style, ...otherAttrs } = this.$attrs
      return otherAttrs
    },

    userOptions() {
      return this.getEntityData(this.user)
    },

    isAdmin() {
      return this.user.is_admin && this.channelUsers
    },

    adminBadgeText() {
      return this.isAdmin ? [{ text: this.$t('channels.channel_user_management.admin'), color: 'secondary' }] : undefined
    },

    userDescription() {
      if (this.$attrs.description) return this.$attrs.description
      if (this.courseEnrollmentUser) return this.courseEnrollmentUserDescription
      if (this.pendingUser) return this.$t('ui.sdui_user_list_item.user_description.pending')
      if (this.expiredUser)
        return this.$t('ui.sdui_user_list_item.user_description.expires', { date: this.$dateFormat.date(this.user.expire_at) })
      return `${this.user.meta.subtitle} ${this.userJoinType}`
    },

    userJoinType() {
      if (!this.channelOptions || !this.user.method) return ''
      const joinType = this.$t(`channels.edit.users_list.user_join_types.${this.user.method}`)
      return `· ${joinType}`
    },

    courseEnrollmentUserDescription() {
      if (!this.courseEnrollmentUser) return

      const formattedJoinedAt = this.$dateFormat.date(this.courseEnrollment.joined_at)
      const formattedLeftAt = this.$dateFormat.date(this.courseEnrollment.left_at)

      // deselected user
      // from ... until
      // TODO this will be used for https://sdui.atlassian.net/browse/SP-4066
      // if (this.courseEnrollment.state === 'DESELECTED') {
      //   return this.$t('classbook.course_selection.enrolled_user_description.deselected_user', {
      //     startDate: formattedJoinedAt,
      //     endDate: formattedLeftAt
      //   })
      // }

      // selected user
      const activeSchoolYear = this.$me.school.getCurrentActiveSchoolYear()
      const joinedAtSchoolYearStart = this.$moment(activeSchoolYear.begins_at).isSame(this.courseEnrollment.joined_at, 'day')
      const leftAtSchoolYearEnd =
        !this.courseEnrollment.left_at || this.$moment(activeSchoolYear.ends_at).isSame(this.courseEnrollment.left_at, 'day')

      // whole school year
      if (joinedAtSchoolYearStart && leftAtSchoolYearEnd) {
        return this.$t('classbook.course_selection.enrolled_user_description.whole_schoolyear')
      }

      const beginsAt = joinedAtSchoolYearStart
        ? this.$t('classbook.course_selection.enrolled_user_description.schoolyear_begin')
        : formattedJoinedAt
      const endsAt = leftAtSchoolYearEnd ? this.$t('classbook.course_selection.enrolled_user_description.schoolyear_end') : formattedLeftAt

      // from ... until
      return `${beginsAt} ${this.$t('classbook.course_selection.enrolled_user_description.to')} ${endsAt}`
    },

    courseEnrollmentUser() {
      return (
        this.user.state === 'ACTIVE' &&
        Object.keys(this.courseEnrollment).length &&
        ['SELECTED', 'DESELECTED'].includes(this.courseEnrollment.state)
      )
    },

    pendingUser() {
      return this.user.state === 'PENDING' && !this.user.meta.subtitle
    },

    expiredUser() {
      return this.user.expire_at
    },

    hasClickEvent() {
      // TODO VUE 3: use $attrs later: Like this: return typeof this.$attrs.onClick === 'function'
      // eslint-disable-next-line
      return typeof this.$listeners.click === 'function';
    },

    ariaPressed() {
      // set to undefined if attr not given (so it doesnt get rendered)
      if (this.$attrs['aria-pressed'] === undefined) return undefined
      return !!this.$attrs['aria-pressed']
    },
  },

  emits: ['click'],

  methods: {},
  created() {},
}
</script>

<style scoped lang="scss">
.sdui-user-item button.sdui-user-item-button {
  min-height: inherit;
  color: $color-gray-400;
}

// disable pointer-events for styled checkbox
.right-action-event-none :deep(.sdui-list-item__right-action-wrapper) {
  pointer-events: none;
}
</style>
